import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, Loader } from '@mantine/core';
import axios from 'axios';

import { getFreightFulfillmentPlanHandlingUnitPresets } from '../../../../api/ltl.api';

import type { FreightFulfillmentPlanHandlingUnitPreset } from '../../../../types/freight-responses';

const SEARCH_DEBOUNCE_MS = 500;

const ITEM_VALUE_SEPARATOR = '|||';

// Disabled so Mantine never fires onOptionSubmit for it.
const NOTHING_FOUND_SENTINEL = `${ITEM_VALUE_SEPARATOR}|NOTHING_FOUND|`;

const buildItemValue = (preset: FreightFulfillmentPlanHandlingUnitPreset): string =>
  `${preset.name}${ITEM_VALUE_SEPARATOR}${preset.handlingUnitPresetId}`;

const extractLabel = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[0];

const extractPresetId = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[1] ?? '';

export type HandlingUnitPresetAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  /** Called with the full record when a saved handling unit preset is picked. */
  onHandlingUnitPresetSelect: (preset: FreightFulfillmentPlanHandlingUnitPreset) => void;
  label: string;
  placeholder: string;
  /** Shown in the dropdown when no presets match or the merchant has none saved. */
  nothingFoundMessage?: string;
  'data-testid'?: string;
};

export const HandlingUnitPresetAutocomplete = ({
  value,
  onChange,
  onHandlingUnitPresetSelect,
  label,
  placeholder,
  nothingFoundMessage,
  'data-testid': dataTestId,
}: HandlingUnitPresetAutocompleteProps) => {
  const [presets, setPresets] = useState<FreightFulfillmentPlanHandlingUnitPreset[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const didJustSubmitRef = useRef(false);
  // Full preset list from the on-mount fetch — restored when the field is cleared.
  const allPresetsRef = useRef<FreightFulfillmentPlanHandlingUnitPreset[]>([]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  const presetById = useMemo(() => new Map(presets.map((preset) => [String(preset.handlingUnitPresetId), preset])), [presets]);

  const data = useMemo(() => {
    if (presets.length > 0) return presets.map(buildItemValue);
    if (hasFetched && !isSearching && nothingFoundMessage) {
      return [{ value: NOTHING_FOUND_SENTINEL, disabled: true }];
    }
    return [];
  }, [presets, isSearching, nothingFoundMessage, hasFetched]);

  const runSearch = useCallback((searchString: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setPresets([]);
    setIsSearching(true);
    getFreightFulfillmentPlanHandlingUnitPresets(searchString, controller.signal)
      .then((results) => {
        if (!controller.signal.aborted) {
          if (searchString === '') allPresetsRef.current = results;
          setPresets(results);
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setPresets([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setHasFetched(true);
          setIsSearching(false);
        }
      });
  }, []);

  useEffect(() => {
    runSearch('');
  }, [runSearch]);

  const handleChange = useCallback(
    (nextValue: string) => {
      if (didJustSubmitRef.current) {
        didJustSubmitRef.current = false;
        return;
      }

      onChange(nextValue);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (nextValue === '') {
        abortControllerRef.current?.abort();
        setPresets(allPresetsRef.current);
        setIsSearching(false);
        return;
      }

      debounceTimerRef.current = setTimeout(() => runSearch(nextValue), SEARCH_DEBOUNCE_MS);
    },
    [onChange, runSearch],
  );

  const handleOptionSubmit = useCallback(
    (itemValue: string) => {
      const preset = presetById.get(extractPresetId(itemValue));
      if (preset) {
        didJustSubmitRef.current = true;
        onChange(preset.name);
        onHandlingUnitPresetSelect(preset);
      }
    },
    [presetById, onChange, onHandlingUnitPresetSelect],
  );

  return (
    <Autocomplete
      data={data}
      data-testid={dataTestId}
      filter={({ options }) => options}
      label={label}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      placeholder={placeholder}
      renderOption={({ option }) => (option.value === NOTHING_FOUND_SENTINEL ? (nothingFoundMessage ?? null) : extractLabel(option.value))}
      rightSection={isSearching ? <Loader size='xs' /> : <FontAwesomeIcon icon={faChevronDown} size='sm' />}
      value={value}
    />
  );
};
